// 画面の文言 (役や問いの文章は src/shared/content/)。言語を足すときは、ja と同じ形のものを足す

import type { Locale } from "../shared/i18n";

const ja = {
  languageLabel: "言語",
  networkError: (status: number) => `通信に失敗しました (${status})`,

  // トップ
  sharedLabel: "シェアされた役",
  findYours: "あなたの役も見つける",
  whosThere: "誰だ？",
  whosThereSource: "── 『ハムレット』冒頭",
  landingIntro: "12の問いで、シェイクスピア36作品の中から<strong>あなたの役</strong>を見つけます。",
  merits: (deadline: string) => [
    {
      icon: "🎭",
      title: "あなたの役がわかる",
      text: "36人の登場人物の中から、あなたにふさわしい役を。二つの顔の秘密も明かします。",
    },
    {
      icon: "✦",
      title: `${deadline}までの登録で「創立メンバー」に`,
      text: "2月1日の本格オープン前に登録した方だけの、特別な称号です。診断を終えた方には、シークレット特典も。",
    },
    {
      icon: "🪪",
      title: "会員番号つきの会員証と、特別なご案内",
      text: "登録が早いほど、若い番号。月に1〜2回、シェイクスピアの世界からの便りが届きます。",
    },
  ],
  registerTitle: "無料で登録して、診断をはじめる",
  email: "メールアドレス",
  nickname: "アダ名",
  nicknameNote: "（サイトの中で表示される名前）",
  nicknamePlaceholder: "例：シェイクスピア好き",
  takenLoginHint: "登録済みの方は、確認コードでログインできます。",
  takenLoginButton: "このメールアドレスでログインする",
  getCode: "確認コードを受け取る",
  registerNote: (deadline: string) =>
    `入力したメールアドレスに、6桁の確認コードをお送りします。メールアドレスは公開されません。${deadline}までは変更できませんので、お間違えのないようにご入力ください。`,
  loginEntry: "すでに登録済みの方は",
  loginEntryLink: "こちらからログイン",
  enterEmailAndNickname: "メールアドレスとアダ名を入力してください",

  // ログイン・確認コード
  login: "ログイン",
  loginNote: "登録したメールアドレスを入れてください。6桁の確認コードをお送りします。",
  backToSignup: "← 新しく登録する",
  enterEmail: "メールアドレスを入力してください",
  signupWithThis: "このメールアドレスで新しく登録する",
  codeTitle: "確認コードを入力",
  codeSent: (email: string) => `<strong>${email}</strong> に、6桁の確認コードをお送りしました。`,
  codeLabel: "確認コード（6桁の数字）",
  completeSignup: "登録を完了する",
  doLogin: "ログインする",
  codeNote: "コードの有効期限は10分です。",
  spamHint:
    "メールが見当たらないときは、<strong>迷惑メールのフォルダ</strong>もご確認ください。そこにあったら「迷惑メールではない」を押していただくと、次からは受信トレイに届きやすくなります。",
  resendCode: "コードを送り直す",
  reenterEmail: "メールアドレスを入れ直す",
  enterCode: "確認コードを入力してください",
  codeResent: "確認コードを送り直しました。",

  // プロスペローの案内
  start: "はじめる",
  guide: (name: string, play: string) => `案内役 ${name}${play}`,
  welcomeTitle: "ようこそ、私の島へ。",
  welcomeText: (nickname: string) => [
    `${nickname}どの。私はプロスペロー、この島の主にして、役を与える者。`,
    "これから十二の問いを投げかけよう。考えすぎず、心のままに答えるがいい。",
    "最後に、シェイクスピアの三十六人の中から、あなたにふさわしい役を与えよう。",
  ],

  // 12問
  progressLabel: (n: number, total: number) => `${n}問目 / 全${total}問`,
  previousQuestion: "← ひとつ前の問いに戻る",
  casting: "プロスペローが、あなたの役を選んでいます…",
  saveFailed: (message: string) => `結果を保存できませんでした：${message}`,
  retry: "もう一度",

  // 配役発表
  yourRoleIs: "あなたの役は、",
  noMask: "裏表のない人",
  roleIntroTitle: (name: string, play: string) => `『${play}』の${name}`,
  costarTitle: "相性のいい共演者",
  bookTitle: "あなたの役の物語を、もっと深く",
  bookText: (play: string) =>
    `『${play}』をはじめ、シェイクスピア全37作品を相関図と濃縮ストーリー、深掘り解説で一気に読める一冊。`,
  bookBy: "この診断をつくった演出家・木村龍之介の本です。",
  bookMain: "Amazon で見る",
  bookOthers: "ほかの書店：",
  bookLink: { amazon: "Amazon", rakuten: "楽天ブックス", publisher: "大和書房" },
  saveCard: "キャラカードを保存・シェア",
  viewCard: "会員証を見る",
  aboutTitle: "この診断について",
  about: [
    "人はみな、人前では「役の鎧」を着て生きています。けれど、ひとりになれば、鎧を脱いだ本当の自分がいる。",
    "シェイクスピアの『お気に召すまま』では、人々は宮廷という「街」を離れ、アーデンの「森」で本当の自分に出会います。この診断の前半の6問は「街のあなた」、後半の6問は「森のあなた」を見つめています。",
    "二つの顔を、王・恋人・道化・策士・戦士・夢見る人の6つのタイプで読み解き、6 × 6 = 36通りの組み合わせに、シェイクスピア36作品から一人ずつを配役しました。善い・悪いで裁くことはしません。悪役も、愚かさも、まるごと人間の魅力です。",
  ],
  retake: "もう一度診断する",
  retakeConfirm: "もう一度診断すると、今の役は新しい結果で置きかわります。よろしいですか？",
  onlyYou: (name: string, diagnosed: number) =>
    `いま<strong>${name}</strong>を演じているのは、<strong class="count">あなただけ</strong>。<small>${
      diagnosed <= 1 ? "（あなたが、診断を終えた最初のひとりです）" : `（診断を終えた ${diagnosed} 人のうち）`
    }</small>`,
  sameCount: (name: string, n: number, diagnosed: number) =>
    `あなたと同じ<strong>${name}</strong>は、いま <strong class="count">${n}</strong> 人。<small>（診断を終えた ${diagnosed} 人のうち）</small>`,
  memberMenu: "会員メニュー",
  tabRole: "あなたの役",
  tabCard: "会員証",
  makingImage: "画像を作っています…",
  imageFailed: "うまく作れませんでした。もう一度",

  // 会員証
  founding: "創立メンバー",
  registeredOn: "登録日",
  castOn: "配役日",
  memberNote: "会員番号は登録順。早い番号ほど、Bard Club の歴史の最初の頁に近い証です。",
  secretTitle: "シークレット特典",
  secretText: "診断を終えたあなただけの特典を準備中です。2月1日の本格オープンの日に、ここで公開されます。",
  messageTitle: "ひとこと",
  optional: "（任意）",
  messageNote: "あなたの好きなもの、今の気分など。今後、ほかの会員との交流の場で表示される予定です。",
  messagePlaceholder: "例：好きな作品は『十二夜』",
  save: "保存",
  saved: "保存しました",
  accountTitle: "登録情報",
  verified: "確認済み",
  unverified: "未確認",
  accountNote: (deadline: string) => `メールアドレスは${deadline}まで変更できません。月に1〜2回、特別なご案内をお送りします。`,
  logout: "この端末からログアウト",
  logoutConfirm: "この端末からログアウトします。次に使うときは、確認コードでログインしてください。",

  // メールのリンクから戻ってきたとき
  mailNotices: {
    unsubscribed: "案内メールの配信を停止しました。",
    "unsubscribe-expired": "配信停止のリンクが無効です。",
  } as Record<string, string>,

  // シェア
  shareTitle: "あなたの役をシェア",
  shareWith: (service: string) => `${service}でシェア`,
  shareLinkLabel: "シェア用のリンク",
  copy: "コピー",
  copied: "コピーしました",
  shareMore: "ほかのアプリでシェア",
  copyDone: "シェア用の文とリンクをコピーしました。好きなところに貼りつけてください。",
  copyFailed: "自動でコピーできませんでした。リンクの欄を選んだので、そのままコピーしてください。",
  makingCard: "キャラカードを作っています…",
  postFromApp: (service: string, url: string) =>
    `画像を保存して、${service}のアプリから投稿してください。投稿文には「${url}」のリンクも添えると、見た人が診断できます。`,
  postFromPc: (service: string, url: string, copied: boolean) =>
    `パソコンからは、${service}の投稿画面を直接開けません。① 画像を保存 → ②「${service}を開く」→ ③ ${service}の投稿（＋ 作成・アップロード）で、保存した画像を選んでください。${
      copied ? "投稿文（リンクつき）はコピー済みなので、貼りつけて使えます。" : `投稿文には「${url}」のリンクも添えると、見た人が診断できます。`
    }`,
  openService: (service: string) => `${service}を開く`,

  // キャラカード画像
  card: "キャラカード",
  cardFailed: "画像を作れませんでした",
  saveToPhotos: "写真やファイルに保存・シェア",
  saveFromShareMenu: "共有メニューから保存・シェア",
  pickAndSave: "保存先を選んで保存",
  saveToDownloads: "ダウンロードフォルダに保存",
  close: "閉じる",
  longPressHint: "画像を長押しして「写真に保存」もできます。",
  pngImage: "PNG 画像",
  saveFailedTryDownload: "保存できませんでした。ダウンロードフォルダに保存をお試しください。",
};

export type UI = typeof ja;

const en: UI = {
  languageLabel: "Language",
  networkError: (status) => `Something went wrong (${status})`,

  sharedLabel: "A role someone shared",
  findYours: "Find your role too",
  whosThere: "Who's there?",
  whosThereSource: "— the first line of Hamlet",
  landingIntro: "Twelve questions find <strong>your role</strong> among 36 characters from Shakespeare.",
  merits: (deadline) => [
    {
      icon: "🎭",
      title: "Discover your role",
      text: "Find the role that fits you among 36 characters, and the secret of your two faces.",
    },
    {
      icon: "✦",
      title: `Join by ${deadline} to become a Founding Member`,
      text: "A special title only for those who join before the grand opening on February 1, plus a secret gift once you're cast.",
    },
    {
      icon: "🪪",
      title: "A numbered membership card, and special letters",
      text: "The earlier you join, the lower your number. Once or twice a month, news from the world of Shakespeare.",
    },
  ],
  registerTitle: "Join for free and start",
  email: "Email address",
  nickname: "Nickname",
  nicknameNote: "(the name shown on the site)",
  nicknamePlaceholder: "e.g. BardLover",
  takenLoginHint: "Already a member? You can log in with a verification code.",
  takenLoginButton: "Log in with this email address",
  getCode: "Send me a code",
  registerNote: (deadline) =>
    `We'll send a 6-digit verification code to this address. Your email address is never shown to others. It can't be changed until ${deadline}, so please check it carefully.`,
  loginEntry: "Already a member?",
  loginEntryLink: "Log in here",
  enterEmailAndNickname: "Please enter your email address and a nickname.",

  login: "Log in",
  loginNote: "Enter the email address you signed up with, and we'll send you a 6-digit code.",
  backToSignup: "← Sign up instead",
  enterEmail: "Please enter your email address.",
  signupWithThis: "Sign up with this email address",
  codeTitle: "Enter your code",
  codeSent: (email) => `We sent a 6-digit code to <strong>${email}</strong>.`,
  codeLabel: "Verification code (6 digits)",
  completeSignup: "Finish signing up",
  doLogin: "Log in",
  codeNote: "The code is valid for 10 minutes.",
  spamHint:
    "Can't find it? Please check your <strong>spam or junk folder</strong>. If it's there, marking it \"Not spam\" helps our mail reach your inbox next time.",
  resendCode: "Send a new code",
  reenterEmail: "Use a different email address",
  enterCode: "Please enter the verification code.",
  codeResent: "We sent you a new code.",

  start: "Begin",
  guide: (name, play) => `Your guide: ${name} ${play}`,
  welcomeTitle: "Welcome to my island.",
  welcomeText: (nickname) => [
    `Greetings, ${nickname}. I am Prospero, master of this island and giver of roles.`,
    "I shall ask you twelve questions. Don't think too hard; answer as your heart tells you.",
    "Then, from Shakespeare's thirty-six, I shall give you the role that suits you.",
  ],

  progressLabel: (n, total) => `Question ${n} of ${total}`,
  previousQuestion: "← Back to the previous question",
  casting: "Prospero is choosing your role…",
  saveFailed: (message) => `We couldn't save your result: ${message}`,
  retry: "Try again",

  yourRoleIs: "Your role is",
  noMask: "No mask",
  roleIntroTitle: (name, play) => (name === play ? name : `${name} in ${play}`),
  costarTitle: "Your best co-star",
  bookTitle: "Go deeper into your role's story",
  bookText: (play) =>
    `A guide to all 37 Shakespeare plays, ${play} included, with character maps, condensed stories and deep dives (in Japanese).`,
  bookBy: "Written by Ryunosuke Kimura, the theatre director who created this casting.",
  bookMain: "View on Amazon",
  bookOthers: "Also at ",
  bookLink: { amazon: "Amazon", rakuten: "Rakuten Books", publisher: "Daiwa Shobo" },
  saveCard: "Save or share your character card",
  viewCard: "See your membership card",
  aboutTitle: "About this diagnosis",
  about: [
    "In public, we all wear the armor of a role. But alone, we take the armor off and become who we really are.",
    "In Shakespeare's As You Like It, people leave the “city” of the court and meet their true selves in the “forest” of Arden. The first six questions look at you in the city; the last six look at you in the forest.",
    "Your two faces are read through six types: King, Lover, Fool, Schemer, Warrior and Dreamer. Each of the 6 × 6 = 36 combinations is cast as one character from 36 of Shakespeare's plays. Nobody here is judged good or bad. Villains and fools alike are part of what makes us human.",
  ],
  retake: "Take the diagnosis again",
  retakeConfirm: "If you take it again, your current role will be replaced by the new result. Continue?",
  onlyYou: (name, diagnosed) =>
    `Right now, <strong class="count">only you</strong> are playing <strong>${name}</strong>. <small>${
      diagnosed <= 1 ? "(You're the very first person to finish the diagnosis.)" : `(out of ${diagnosed} people cast so far)`
    }</small>`,
  sameCount: (name, n, diagnosed) =>
    `<strong class="count">${n}</strong> people are playing <strong>${name}</strong> right now. <small>(out of ${diagnosed} people cast so far)</small>`,
  memberMenu: "Member menu",
  tabRole: "Your role",
  tabCard: "Membership card",
  makingImage: "Making your image…",
  imageFailed: "That didn't work. Try again",

  founding: "Founding Member",
  registeredOn: "Joined",
  castOn: "Cast",
  memberNote: "Numbers are given in the order people join. The lower yours is, the closer you are to the first page of Bard Club's story.",
  secretTitle: "Secret gift",
  secretText: "A gift just for members who've been cast is on its way. It will be revealed here on February 1, our grand opening.",
  messageTitle: "A few words",
  optional: "(optional)",
  messageNote: "Something you love, or how you feel today. It will be shown in a members' space we're planning.",
  messagePlaceholder: "e.g. My favorite play is Twelfth Night",
  save: "Save",
  saved: "Saved",
  accountTitle: "Your account",
  verified: "Verified",
  unverified: "Not verified",
  accountNote: (deadline) =>
    `Your email address can't be changed until ${deadline}. We'll send you special letters once or twice a month.`,
  logout: "Log out on this device",
  logoutConfirm: "You'll be logged out on this device. Next time, log in with a verification code.",

  mailNotices: {
    unsubscribed: "You've been unsubscribed from our letters.",
    "unsubscribe-expired": "This unsubscribe link is no longer valid.",
  },

  shareTitle: "Share your role",
  shareWith: (service) => `Share on ${service}`,
  shareLinkLabel: "Link to share",
  copy: "Copy",
  copied: "Copied",
  shareMore: "Share with another app",
  copyDone: "The text and link are copied. Paste them wherever you like.",
  copyFailed: "We couldn't copy automatically. The link is selected, so please copy it yourself.",
  makingCard: "Making your character card…",
  postFromApp: (service, url) =>
    `Save the image and post it from the ${service} app. Add the link ${url} so others can find their role too.`,
  postFromPc: (service, url, copied) =>
    `${service} can't open its post screen from a computer browser. 1) Save the image, 2) click “Open ${service}”, 3) create a new post or upload there and choose the saved image. ${
      copied ? "The post text (with the link) is already copied, so you can paste it in." : `Add the link ${url} so others can find their role too.`
    }`,
  openService: (service) => `Open ${service}`,

  card: "Character card",
  cardFailed: "Couldn't make the image",
  saveToPhotos: "Save to Photos or Files, or share",
  saveFromShareMenu: "Save or share from the share menu",
  pickAndSave: "Choose where to save",
  saveToDownloads: "Save to Downloads",
  close: "Close",
  longPressHint: "You can also press and hold the image to save it to Photos.",
  pngImage: "PNG image",
  saveFailedTryDownload: "Couldn't save. Please try “Save to Downloads”.",
};

export const UI_TEXT: Record<Locale, UI> = { ja, en };
